import React, { useMemo } from "react";
import { CalendarDays, ChevronDown, ChevronUp } from "lucide-react";
import { motion } from "framer-motion";
import DayCard from "./DayCard";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * "Your Challenge Calendar" panel. Minimizable: when collapsed it shows only
 * the current week (today stays clickable) so you can keep marking days
 * without the full grid. The collapsed state is cloud-persisted by App.js.
 */
const CalendarPanel = ({
  weeks,
  completedDays,
  partialDays,
  onToggleDay,
  collapsed,
  onToggleCollapsed,
}) => {
  // Week shown while collapsed: the one containing today; else the most
  // recent past week (challenge finished); else the first (not started yet).
  const focusWeek = useMemo(() => {
    if (!weeks || weeks.length === 0) return [];
    const byToday = weeks.find((w) => w.some((d) => d && d.isToday));
    if (byToday) return byToday;
    const lastPast = [...weeks]
      .reverse()
      .find((w) => w.some((d) => d && d.isPast));
    return lastPast || weeks[0];
  }, [weeks]);

  const weekDays = focusWeek.filter(Boolean);
  const doneInWeek = weekDays.filter((d) => completedDays.has(d.date)).length;

  const renderWeek = (week, keyPrefix, compact) => (
    <div className="grid grid-cols-7 gap-1 sm:gap-3">
      {week.map((day, i) =>
        day ? (
          <DayCard
            key={day.date}
            day={day}
            compact={compact}
            isCompleted={completedDays.has(day.date)}
            isPartial={partialDays.has(day.date)}
            onToggle={() => onToggleDay(day.date)}
          />
        ) : (
          <div
            key={`${keyPrefix}-empty-${i}`}
            className="opacity-0 pointer-events-none"
            aria-hidden="true"
          >
            <div className={compact ? "w-full min-h-[62px]" : "w-full aspect-square"} />
          </div>
        ),
      )}
    </div>
  );

  return (
    <section
      data-panel-section
      data-panel-key="calendar"
      data-panel-label="Calendar"
      className="bg-gray-800 rounded-3xl p-3 sm:p-5 border-2 border-cyan-500/30 shadow-lg card-shadow"
    >
      <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
        <h3 className="text-lg sm:text-xl font-bold text-yellow-400 flex items-center gap-2 min-w-0">
          <CalendarDays className="w-5 h-5 flex-shrink-0" />
          <span className="truncate">Your Challenge Calendar</span>
        </h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          {collapsed && weekDays.length > 0 && (
            <span className="hidden sm:inline text-xs text-gray-400">
              This week · {doneInWeek}/{weekDays.length} done
            </span>
          )}
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls="calendar-grid"
            className="tap px-3 py-2 rounded-2xl bg-gray-700 hover:bg-gray-600 text-cyan-300 border-2 border-cyan-500/30 hover:border-cyan-500 text-sm font-semibold flex items-center gap-1.5"
          >
            {collapsed ? (
              <>
                <ChevronDown className="w-4 h-4" />
                Expand
              </>
            ) : (
              <>
                <ChevronUp className="w-4 h-4" />
                Minimize
              </>
            )}
          </button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 sm:gap-3 mb-2">
        {DAY_NAMES.map((dayName) => (
          <div
            key={dayName}
            className="text-center text-[11px] sm:text-sm font-semibold text-cyan-300"
          >
            {dayName}
          </div>
        ))}
      </div>

      {/* Entrance-only transition (no exit dependency) so the swap is instant
          and robust even when the tab is throttled or in the background. */}
      {collapsed ? (
        <motion.div
          key="week"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {renderWeek(focusWeek, "week", true)}
        </motion.div>
      ) : (
        <motion.div
          key="grid"
          id="calendar-grid"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="space-y-2 sm:space-y-3"
        >
          {weeks.map((week, weekIndex) => (
            <React.Fragment key={weekIndex}>
              {renderWeek(week, `w${weekIndex}`, false)}
            </React.Fragment>
          ))}
        </motion.div>
      )}
    </section>
  );
};

export default CalendarPanel;
